import { flowResult } from 'mobx';
import { rootStore } from '../mobx/rootStore';
import { getAllProjectRawTasks, getAllRawTasks } from './taskServices';
import { RawTask } from '../mobx/interfaces/task';
import MobxTasksCacheService from './mobxTasksCacheService';
import MobxProjectsCacheService from './mobxProjectsCacheService';
import { getAllRawProjects } from './projectServices';
import { TaskModel } from '../mobx/models/task';
import { RawProject } from '../mobx/interfaces/project';
import { getWorkspaceAllProjectsPreferenceData } from '../helpers/firebaseHelper';
import { ProjectPreferenceData } from '../mobx/stores/projectStore';
import { captureException } from './logService';
import { getSocketConnectedAt } from './realtimeStatus';

// The realtime socket keeps tasks current, but refetch after this long anyway in case events were silently dropped
const MAX_TASKS_STALE_MS = 5 * 60 * 1000;

type SyncOptions = {
  loadCached?: boolean;
  // Skip joining an in-flight fetch, e.g. right after a mutation so the result includes it
  force?: boolean;
};

// Shared across callers so concurrent syncs (e.g. appInit + page mount on refresh) hit getAllTasks once.
// Scoped by workspace since the task cache is per-workspace.
let inFlightTaskFetch: { workspaceId?: string; promise: Promise<void> } | null = null;

const dedupeTaskFetch = (fetcher: (workspaceId?: string) => Promise<void>, force = false) => {
  const workspaceId = rootStore.workspaceStore.currentWorkspace?.id;
  if (force || !inFlightTaskFetch || inFlightTaskFetch.workspaceId !== workspaceId) {
    const promise = fetcher(workspaceId).finally(() => {
      if (inFlightTaskFetch?.promise === promise) inFlightTaskFetch = null;
    });
    inFlightTaskFetch = { workspaceId, promise };
  }
  return inFlightTaskFetch.promise;
};

const isWorkspaceChanged = (workspaceId?: string) => workspaceId !== rootStore.workspaceStore.currentWorkspace?.id;

let lastTaskSync: { workspaceId?: string; startedAt: number } | null = null;

// True when a sync started after the socket connected (so no events were missed since) and is still recent
const isTaskStoreCurrent = () => {
  const connectedAt = getSocketConnectedAt();
  if (!lastTaskSync || connectedAt === null || isWorkspaceChanged(lastTaskSync.workspaceId)) return false;
  return lastTaskSync.startedAt >= connectedAt && Date.now() - lastTaskSync.startedAt < MAX_TASKS_STALE_MS;
};

// Stamp with the request start time so changes made while the request was in flight are picked up next time
const markTasksSynced = (workspaceId: string | undefined, startedAt: number) => {
  lastTaskSync = { workspaceId, startedAt };
  MobxTasksCacheService.getInstance().updateLastUpdatedTime(new Date(startedAt));
};

// Sync Tasks Data
export const syncTasks = async (options: SyncOptions = {}) => {
  const loadCached = typeof options.loadCached === 'boolean' ? options.loadCached : false;
  const force = options.force === true;

  const [lastTasksUpdatedDate, localTasks] = await Promise.all([
    MobxTasksCacheService.getInstance().getLastUpdatedTime(),
    MobxTasksCacheService.getInstance().getTasks()
  ]);
  if (lastTasksUpdatedDate && localTasks?.length > 0) {
    if (loadCached) {
      await flowResult(rootStore.taskStore.updateWithIndexdb());
    }

    if (!force && isTaskStoreCurrent()) return;

    // Background Fetch
    dedupeTaskFetch(async (workspaceId) => {
      const startedAt = Date.now();
      const allRawTasks = await getAllRawTasks(lastTasksUpdatedDate);
      if (!allRawTasks || isWorkspaceChanged(workspaceId)) return;

      const { newRawTasks, archivedTasks } = allRawTasks.reduce(
        (acc, task) => {
          if (task.isArchived) {
            acc.archivedTasks.push(task);
          } else {
            acc.newRawTasks.push(task);
          }

          return acc;
        },
        { newRawTasks: [] as RawTask[], archivedTasks: [] as RawTask[] }
      );

      const excludeFields: (keyof TaskModel)[] = ['comments', 'pullRequests', 'files', 'timeLogs'];
      rootStore.taskStore.syncList(newRawTasks, archivedTasks, { excludeFields });

      markTasksSynced(workspaceId, startedAt);
    }, force).catch(captureException);
  } else {
    // Fetch all tasks
    await dedupeTaskFetch(async (workspaceId) => {
      const startedAt = Date.now();
      const allRawTasks = await getAllRawTasks();
      if (!allRawTasks || isWorkspaceChanged(workspaceId)) return;

      rootStore.taskStore.setTasks(allRawTasks);
      markTasksSynced(workspaceId, startedAt);
    }, force);
  }
};

// Events emitted before the socket (re)connected were missed. Let any in-flight fetch finish first,
// then the gate refetches only if that fetch started before the connect.
export const syncTasksOnSocketConnect = async () => {
  await inFlightTaskFetch?.promise.catch(() => undefined);
  await syncTasks();
};

export const syncProjects = async (options: SyncOptions = {}) => {
  const loadCached = typeof options.loadCached === 'boolean' ? options.loadCached : false;

  const lastProjectsUpdatedDate = await MobxProjectsCacheService.getInstance().getLastUpdatedTime();

  if (!lastProjectsUpdatedDate) {
    await fetchProjectsAndUpdateStore(lastProjectsUpdatedDate);
  } else {
    if (loadCached) {
      await flowResult(rootStore.projectStore.updateWithIndexdb());
    }

    fetchProjectsAndUpdateStore(lastProjectsUpdatedDate);
  }
};

const fetchProjectsAndUpdateStore = async (lastProjectsUpdatedDate: Date | null) => {
  const workspace_id = rootStore.workspaceStore.currentWorkspace?.id;
  const allRawProjects = await getAllRawProjects();
  if (!allRawProjects) return;

  let updatedProjects: RawProject[] = [];

  if (lastProjectsUpdatedDate && rootStore.projectStore.projects.length > 0) {
    // Also include projects missing from the store; upsertProjects fetches their tasks
    const currentProjectIds = new Set(rootStore.projectStore.projects.map((project) => project.id));
    updatedProjects = allRawProjects.filter(
      (project) => !currentProjectIds.has(project.id) || new Date(project.updatedOn) >= lastProjectsUpdatedDate
    );
  } else {
    rootStore.projectStore.setProjects(allRawProjects);
  }
  if (workspace_id && workspace_id !== rootStore.workspaceStore.currentWorkspace?.id) return;
  const allProjectIds = allRawProjects.map((project) => project.id);
  const removedProjects = rootStore.projectStore.projects.filter((project) => !allProjectIds.includes(project.id));

  rootStore.projectStore.syncList(updatedProjects, removedProjects);

  // One-time bulk apply of stored preferences after a full project fetch. This is a
  // deliberate single read (not driven by the listener), so passing the whole map here
  // does not reintroduce the listener-to-read cascade.
  const allProjectsPreferenceData = (await getWorkspaceAllProjectsPreferenceData()) as Record<
    string,
    ProjectPreferenceData
  > | null;
  if (allProjectsPreferenceData) {
    rootStore.projectStore.updateWithFirebase(allProjectsPreferenceData);
  }

  MobxProjectsCacheService.getInstance().updateLastUpdatedTime();
};

export const syncTasksForProjects = async (projectIds: string[], workspace_id?: string) => {
  const allRawTasks = await getAllProjectRawTasks(projectIds);
  if (workspace_id && workspace_id !== rootStore.workspaceStore.currentWorkspace?.id) return;
  rootStore.taskStore.upsertTasks(allRawTasks);
};
